import type { Writable } from 'svelte/store';
import { writable } from 'svelte/store';
import type { ButtonOrLinkAppearance } from '../types/props';

export type SnackbarStore = {
	open: (_: Partial<SnackbarOptions>) => void;
	success: (message: string, _?: Partial<SnackbarOptions>) => void;
	danger: (message: string, _?: Partial<SnackbarOptions>) => void;
	warning: (message: string, _?: Partial<SnackbarOptions>) => void;
	reset: () => void;
};

export type SnackbarOptions = {
	isOpen: boolean;
	message: string;
	color: string;
	ttl: number;
	textColor?: string;
	ctaLabel?: string;
	ctaAction?: () => void;
	ctaUrl?: string;
	ctaUrlTargetBlank?: boolean;
	ctaColor?: string;
	ctaAppearance?: ButtonOrLinkAppearance;
	iconName?: string;
};

const snackbarOptionsDefault: SnackbarOptions = {
	isOpen: false,
	message: '',
	color: 'secondary',
	ttl: 8000,
	textColor: 'white',
	ctaAppearance: 'outlined',
	ctaColor: 'white',
	ctaLabel: undefined,
	ctaAction: undefined,
	ctaUrl: undefined,
	ctaUrlTargetBlank: true,
	iconName: undefined
};

const snackbarStore = (options: SnackbarOptions): SnackbarStore & Writable<SnackbarOptions> => {
	const store = writable<SnackbarOptions>(options);

	const open = (options: Partial<SnackbarOptions>) => {
		store.update(() => ({ ...snackbarOptionsDefault, ...options, isOpen: true }));
	};

	const success = (message: string, options?: Partial<SnackbarOptions>) => {
		store.update(() => ({
			...snackbarOptionsDefault,
			...options,
			isOpen: true,
			color: 'success',
			message
		}));
	};
	const warning = (message: string, options?: Partial<SnackbarOptions>) => {
		store.update(() => ({
			...snackbarOptionsDefault,
			...options,
			isOpen: true,
			color: 'warning',
			message
		}));
	};

	const danger = (message: string, options?: Partial<SnackbarOptions>) => {
		store.update(() => ({
			...snackbarOptionsDefault,
			...options,
			isOpen: true,
			color: 'danger',
			message
		}));
	};

	const reset = () => {
		store.update(() => ({ ...snackbarOptionsDefault }));
	};

	return {
		...store,
		open,
		success,
		danger,
		warning,
		reset
	};
};

export const snackbar = snackbarStore(snackbarOptionsDefault);
